import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SearchableSelect.css';

export const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Caută...',
  emptyLabel = 'Nu există opțiuni',
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  useEffect(() => {
    if (!isOpen) {
      setSearch(selectedOption?.label || '');
    }
  }, [selectedOption, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch(selectedOption?.label || '');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, [options, search]);

  return (
    <div className="search-select" ref={containerRef}>
      <input
        type="text"
        className="search-select-input"
        placeholder={placeholder}
        value={search}
        onFocus={() => setIsOpen(true)}
        onChange={(event) => {
          setSearch(event.target.value);
          setIsOpen(true);
        }}
      />

      {isOpen && (
        <div className="search-select-dropdown">
          {filtered.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`search-select-option ${option.value === value ? 'active' : ''}`}
              onClick={() => {
                onChange(option.value);
                setSearch(option.label);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
          {!filtered.length && <div className="search-select-empty">{emptyLabel}</div>}
        </div>
      )}
    </div>
  );
};

export const SearchableMultiSelect = ({
  options,
  values,
  onChange,
  placeholder = 'Selectează...',
  emptyLabel = 'Nu există opțiuni',
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const safeValues = useMemo(() => values || [], [values]);
  const selectedValues = useMemo(() => new Set(safeValues), [safeValues]);

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedValues.has(option.value)),
    [options, selectedValues]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, [options, search]);

  const toggleValue = (optionValue) => {
    const nextValues = new Set(selectedValues);
    if (nextValues.has(optionValue)) {
      nextValues.delete(optionValue);
    } else {
      nextValues.add(optionValue);
    }
    onChange(Array.from(nextValues));
  };

  const removeValue = (event, optionValue) => {
    event.stopPropagation();
    const nextValues = new Set(selectedValues);
    nextValues.delete(optionValue);
    onChange(Array.from(nextValues));
  };

  return (
    <div className="search-select" ref={containerRef}>
      {/* Trigger: shows chips or placeholder, click opens dropdown */}
      <div
        className={`multi-select-trigger${isOpen ? ' open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsOpen((prev) => !prev)}
      >
        {selectedOptions.length === 0 && (
          <span className="multi-select-placeholder">{placeholder}</span>
        )}
        {selectedOptions.map((option) => (
          <span key={option.value} className="multi-chip">
            {option.label}
            <button
              type="button"
              className="multi-chip-remove"
              onClick={(e) => removeValue(e, option.value)}
              tabIndex={-1}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {/* Dropdown: search input on top, checkbox list below */}
      {isOpen && (
        <div className="multi-select-dropdown">
          <input
            ref={searchRef}
            type="text"
            className="multi-select-search"
            placeholder="Caută..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="multi-select-list">
            {filtered.map((option) => (
              <label key={option.value} className="multi-select-option">
                <input
                  type="checkbox"
                  checked={selectedValues.has(option.value)}
                  onChange={() => toggleValue(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
            {!filtered.length && <div className="search-select-empty">{emptyLabel}</div>}
          </div>
        </div>
      )}
    </div>
  );
};
